'use client';

import { Stack } from '@mui/material';
import dynamic from 'next/dynamic';
import { useCallback } from 'react';

import { DigitalhubDivider } from '@dh/components';
import { useBreakpoint } from '@dh/hooks';
import { Column, Row } from '@dh/styles';
import { isCompact, isExpanded, Project, Topic, User } from '@dh/types';

import { DashboardScreenFavouritesSection } from './favourites-section/DashboardScreenFavouritesSection';
import { DashboardScreenGreetingSection } from './greeting-section/GreetingSection';
import { InterestedTopicsProps } from './interested-topic-section/DashboardScreenInterestedTopics';
import { DashboardScreenIntroduction } from './onboarding-section/DashboardScreenIntroduction';
import { DashboardProjectsSection } from './projects-section/DashboardProjectsSection';
import { DashboardScreenMatchingSection } from './matching-section/DashboardScreenMatchingSection';
import { DashboardContainer } from './styles';
import { LandingPageLoggedInFooterSection } from '../landing-page/landing-page-sections/LandingPageLoggedInFooterSection';

const InterestedTopicSection = dynamic<InterestedTopicsProps>(
    () =>
        import('../dashboard/interested-topic-section/DashboardScreenInterestedTopics').then(
            (module) => module.DashboardScreenInterestedTopics
        ),
    { ssr: false }
);

interface Props {
    suggestableProjects: Project[];
    user: User;
    topics: Topic[];
}

export const DashboardScreen = ({ suggestableProjects, user, topics }: Props) => {
    const breakpoint = useBreakpoint();
    const expanded = isExpanded(breakpoint);
    const compact = isCompact(breakpoint);
    const isCompany = user.institution?.type === 'company';

    const renderLeftContent = useCallback(
        () => (
            <Stack width={expanded ? '68%' : '100%'} gap={2}>
                {expanded && <DashboardScreenIntroduction />}
                <DashboardProjectsSection suggestableProjects={suggestableProjects} />

                <DashboardScreenFavouritesSection
                    projects={user.favouriteProjects}
                    companies={user.favouriteCompanies}
                />
            </Stack>
        ),
        [expanded, suggestableProjects, user.favouriteProjects, user.favouriteCompanies]
    );

    const renderRightContent = useCallback(
        () => (
            <Stack width={expanded ? '32%' : '100%'} minWidth={310} gap={2} px={compact ? 0 : 2}>
                {!expanded && <DashboardScreenIntroduction />}
                {user.institution && <DashboardScreenMatchingSection institution={user.institution} />}
                {topics.length > 0 && !isCompany && <InterestedTopicSection topics={topics} />}
            </Stack>
        ),
        [expanded, compact, user.institution, topics, isCompany]
    );

    return (
        <DashboardContainer>
            <DashboardScreenGreetingSection firstname={user.firstname} lastname={user.lastname} />
            {expanded ? (
                <Row mt={3} style={{ alignItems: 'flex-start' }}>
                    {renderLeftContent()}
                    <DigitalhubDivider orientation={'vertical'} />
                    {renderRightContent()}
                </Row>
            ) : (
                <Column gap={2} mt={2}>
                    {renderRightContent()}
                    {renderLeftContent()}
                </Column>
            )}
            <LandingPageLoggedInFooterSection />
        </DashboardContainer>
    );
};
